import { useMemo } from 'react'

import {
  Box,
  Flex,
  FloraInput,
  FloraRadio,
  FloraSelect,
  FloraSelectItem,
  FloraTypography
} from '@grupoboticario/flora-react'

import HeadingAccelerator from '@/components/HeadingAccelerator/HeadingAccelerator'
import { EXTRACT_DESCRIPTION_MAX } from '@/constants/accelerationForm'
import { ACCELERATION_STEPPER_VARIANT } from '@/constants/accelerationStepper'
import {
  AUDIENCE_TYPES_LIST,
  CAMPAIGN_AUDIENCE_TYPES_LIST,
  CAMPAIGN_TYPE,
  CAMPAIGN_TYPES_LIST,
  CAMPAIGN_MULTIPLIER_VALUES,
  POINTS_VALIDITY_OPTIONS,
  POINTS_VALIDITY_TYPE,
  POINTS_TYPE,
  STATUS_LABEL
} from '@/constants/accelerationTable'
import { formatDateTimeWithLocale } from '@/utils/format/formatDateTimeWithLocale'

import * as S from '../AccelerationForm.styles'
import {
  AccelerationFormPointsValues,
  AccelerationFormSegmentationValues,
  AccelerationFormTopLevelField,
  AccelerationFormState,
  AccelerationFormFields,
  AccelerationStepperVariant,
  AccelerationFormChangeHistory
} from '../AccelerationForm.types'
import CharacterCounter from './counter/CharacterCounter'
import FlexiblePoints from './FlexiblePoints'
import MultiplierCalculationExample from './MultiplierCalculationExample/MultiplierCalculationExample'

interface StepScoringProps {
  values: AccelerationFormState
  onChange: (field: AccelerationFormTopLevelField, value: string) => void
  onSegmentationChange: (field: keyof AccelerationFormSegmentationValues, value: string) => void
  onPointsChange: (field: keyof AccelerationFormPointsValues, value: string) => void
  onStartDateChange: (value: string) => void
  onEndDateChange: (value: string) => void
  onValidityStartDateChange: (value: string) => void
  onMultiplierChange: (value: string) => void
  onTypeChange: (value: string) => void
  isReadOnly?: boolean
  initialValues?: AccelerationFormFields
  variant: AccelerationStepperVariant
}

const StepScoring = ({
  values,
  onChange,
  onSegmentationChange,
  onPointsChange,
  onStartDateChange,
  onEndDateChange,
  onValidityStartDateChange,
  onMultiplierChange,
  onTypeChange,
  variant,
  isReadOnly = false,
  initialValues = {}
}: StepScoringProps) => {
  const today = useMemo(() => new Date().toISOString().split('T')[0], [])

  const splitDate = (date: string) => date?.split('T')[0]

  const getMinDateForPointsEndValidityDate = () => {
    const dates = [values.points.startDate, values.startDate, values.endDate].filter(Boolean)
    if (dates.length === 0) return today
    const maxDate = new Date(Math.max(...dates.map((date) => new Date(date).getTime())))
    return maxDate.toISOString().split('T')[0]
  }

  const handleFieldChange =
    (field: AccelerationFormTopLevelField) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      onChange(field, e.target.value)
    }

  const handlePointsFieldChange =
    (field: keyof AccelerationFormPointsValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onPointsChange(field, e.target.value)
    }

  const handlePointInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' || e.key === '.') e.preventDefault()
  }

  const isValidCountOfDaysValue = (value: string): boolean => {
    if (!/^\d+$/.test(value)) return false
    const parsedValue = Number.parseInt(value, 10)
    return Number.isInteger(parsedValue) && parsedValue >= 1
  }

  const handleCountOfDaysKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault()
  }

  const processTypeFixed = () => {
    onPointsChange('type', POINTS_TYPE.DEFAULT)
    onPointsChange('countOfDays', '')
  }

  const processTypeFlexible = () => {
    onPointsChange('type', POINTS_TYPE.TIER_RULE)
    onPointsChange('countOfDays', '')
    onPointsChange('startDate', '')
    onPointsChange('endDate', '')
  }

  const handlePointsValidityTypeChange = (value: string) => {
    onChange('pointsValidityType', value)

    if (value === POINTS_VALIDITY_TYPE.FIXED) {
      processTypeFixed()
      return
    }

    if (value === POINTS_VALIDITY_TYPE.FLEXIBLE) {
      processTypeFlexible()
    }
  }

  const handleFlexibleRuleChange = (value: string) => {
    onPointsChange('type', value)
    if (value !== POINTS_TYPE.DAYS_CALENDAR) {
      onPointsChange('countOfDays', '')
    }
  }

  const handleNumberOfDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || isValidCountOfDaysValue(value)) {
      onPointsChange('countOfDays', value)
    }
  }

  const getChangeHistoryAction = (change: AccelerationFormChangeHistory) => {
    if (change.justification?.length) {
      return 'Cancelado'
    }

    return 'Editado'
  }

  const isFlexiblePointsValidity = values.pointsValidityType === POINTS_VALIDITY_TYPE.FLEXIBLE
  const isDaysCalendarRule = values.points.type === POINTS_TYPE.DAYS_CALENDAR
  const isPointsRuleSelected = values.points.type

  const SCORE_FIELD_MAP = {
    [CAMPAIGN_TYPE.MULTIPLIER]: (
      <FloraSelect
        as="div"
        data-testid="multiplierInput"
        disabled={isReadOnly}
        id="multiplierScore"
        label="Multiplicador"
        onSelected={(selected) => onMultiplierChange(selected?.value?.toString() ?? '')}
        placeholder="Selecione"
        required
        size="medium"
        value={values.multiplier}
      >
        {CAMPAIGN_MULTIPLIER_VALUES.map((multiplier) => (
          <FloraSelectItem key={multiplier.value} label={multiplier.label} value={multiplier.value.toString()} />
        ))}
      </FloraSelect>
    ),
    [CAMPAIGN_TYPE.POINT]: (
      <FloraInput
        data-testid="scoreInput"
        disabled={isReadOnly}
        id="point"
        label="Pontuação"
        onChange={handleFieldChange('point')}
        onKeyDown={handlePointInput}
        placeholder="Digite a pontuação"
        readOnly={isReadOnly}
        required
        size="medium"
        step="1"
        type="number"
        value={values.point}
      />
    )
  } as const

  return (
    <Flex direction="column" gap="$3">
      <Flex direction="column" gap="$3">
        <HeadingAccelerator title="Audiência" />
        <Flex gap="$3">
          <Box css={S.fullWidthCss}>
            <FloraSelect
              as="div"
              data-testid="campaignTypeInput"
              disabled={isReadOnly}
              id="campaignType"
              label="Tipo de campanha"
              onSelected={(selected) => onSegmentationChange('campaignType', selected?.value ?? '')}
              placeholder="Selecionar"
              required
              size="medium"
              value={values.segmentation.campaignType}
            >
              {CAMPAIGN_AUDIENCE_TYPES_LIST.map((type) => (
                <FloraSelectItem key={type.value} label={type.label} value={type.value} />
              ))}
            </FloraSelect>
          </Box>

          <Box css={S.fullWidthCss}>
            <FloraSelect
              as="div"
              data-testid="audienceTypeInput"
              disabled={isReadOnly}
              id="audienceType"
              label="Tipo de audiência"
              onSelected={(selected) => onSegmentationChange('audienceType', selected?.value ?? '')}
              placeholder="Selecionar"
              required
              size="medium"
              value={values.segmentation.audienceType}
            >
              {AUDIENCE_TYPES_LIST.map((type) => (
                <FloraSelectItem key={type.value} label={type.label} value={type.value} />
              ))}
            </FloraSelect>
          </Box>
        </Flex>
      </Flex>

      <Flex direction="column" gap="$3">
        <HeadingAccelerator title="Pontuação" />

        <Flex gap="$3">
          <FloraInput
            data-testid="nameInput"
            disabled={isReadOnly}
            id="name"
            onChange={handleFieldChange('name')}
            label="Nome do acelerador"
            readOnly={isReadOnly}
            required
            size="medium"
            value={values.name}
          />

          {isReadOnly && (
            <>
              <FloraInput
                data-testid="campaignIdInput"
                disabled
                id="campaignId"
                label="Código da campanha"
                readOnly
                required
                size="medium"
                value={initialValues.campaignId || ''}
              />
              <FloraInput
                data-testid="campaignStatusInput"
                disabled
                id="campaignStatus"
                label="Status da campanha"
                readOnly
                required
                size="medium"
                value={initialValues.status ? STATUS_LABEL[initialValues.status] : ''}
              />
            </>
          )}
        </Flex>

        <Flex gap="$3">
          <Box css={S.fullWidthCss}>
            <FloraInput
              data-testid="startDateInput"
              disabled={isReadOnly}
              id="startDate"
              label="Data de início:"
              min={today}
              onChange={(e) => onStartDateChange(e.target.value)}
              onKeyDown={(e) => e.preventDefault()}
              readOnly={isReadOnly}
              required
              size="medium"
              type="date"
              value={
                isReadOnly || variant === ACCELERATION_STEPPER_VARIANT.EDIT
                  ? splitDate(values.startDate)
                  : values.startDate
              }
            />
          </Box>
          <FloraInput
            data-testid="endDateInput"
            disabled={isReadOnly}
            id="endDate"
            label="Data de término:"
            min={values.startDate || today}
            onChange={(e) => onEndDateChange(e.target.value)}
            onKeyDown={(e) => e.preventDefault()}
            readOnly={isReadOnly}
            required
            size="medium"
            type="date"
            value={
              isReadOnly || variant === ACCELERATION_STEPPER_VARIANT.EDIT ? splitDate(values.endDate) : values.endDate
            }
          />
        </Flex>

        <Flex gap="$3">
          <Box css={S.fullWidthCss}>
            <FloraSelect
              as="div"
              data-testid="typeScoreInput"
              disabled={isReadOnly}
              id="typeScore"
              label="Tipo de pontuação"
              onSelected={(selected) => onTypeChange(selected?.value ?? '')}
              placeholder="Selecionar"
              required
              size="medium"
              value={values.type}
            >
              {CAMPAIGN_TYPES_LIST.map((type) => (
                <FloraSelectItem key={type.value} label={type.label} value={type.value} />
              ))}
            </FloraSelect>
          </Box>

          <Box css={S.fullWidthCss}>
            <Flex direction="column" gap="$2">
              {SCORE_FIELD_MAP[values.type as keyof typeof SCORE_FIELD_MAP] ?? (
                <FloraInput
                  data-testid="scoreInput"
                  disabled
                  id="point"
                  label="Pontuação"
                  type="number"
                  size="medium"
                />
              )}
              {values.type === CAMPAIGN_TYPE.MULTIPLIER && (
                <MultiplierCalculationExample multiplier={values.multiplier} />
              )}
            </Flex>
          </Box>
        </Flex>
      </Flex>

      <Flex direction="column" gap="$3">
        <HeadingAccelerator title="Extrato" />
        <Box css={S.fullWidthCss}>
          <FloraInput
            data-testid="extractDescriptionInput"
            disabled={isReadOnly}
            id="extractDescription"
            label="Descrição no extrato"
            maxLength={EXTRACT_DESCRIPTION_MAX}
            onChange={handlePointsFieldChange('description')}
            readOnly={isReadOnly}
            required
            size="medium"
            value={values.points.description}
          />
          {!isReadOnly && <CharacterCounter current={values.points.description.length} max={EXTRACT_DESCRIPTION_MAX} />}
        </Box>
        <Flex align="center" gap="$1">
          <FloraTypography as="span" fontSize="bodySmallStandard" fontWeight="medium">
            Validade dos pontos
          </FloraTypography>
          <FloraTypography as="span" fontSize="bodySmallStandard" css={{ color: '$statusErrorActionableDefault' }}>
            *
          </FloraTypography>
        </Flex>

        <Flex gap="$4">
          {POINTS_VALIDITY_OPTIONS.map((option) => (
            <FloraRadio
              key={option.id}
              checked={values.pointsValidityType === option.value}
              disabled={isReadOnly}
              id={option.id}
              label={option.label}
              name="pointsValidityType"
              onChange={() => handlePointsValidityTypeChange(option.value)}
              required
              value={option.value}
            />
          ))}
        </Flex>

        {isFlexiblePointsValidity ? (
          <FlexiblePoints
            countOfDays={values.points.countOfDays}
            isDaysCalendarRule={isDaysCalendarRule}
            isReadOnly={isReadOnly}
            onCountOfDaysKeyDown={handleCountOfDaysKeyDown}
            onFlexibleRuleChange={handleFlexibleRuleChange}
            onNumberOfDaysChange={handleNumberOfDaysChange}
            pointsType={values.points.type}
          />
        ) : (
          <Flex gap="$3" css={{ marginTop: '$5' }}>
            <FloraInput
              data-testid="startValidityDateInput"
              disabled={isReadOnly || !isPointsRuleSelected}
              id="startValidityDate"
              label="Início da vigência dos pontos"
              min={values.startDate || today}
              onChange={(e) => onValidityStartDateChange(e.target.value)}
              onKeyDown={(e) => e.preventDefault()}
              readOnly={isReadOnly}
              required
              size="medium"
              type="date"
              value={values.points.startDate}
            />
            <FloraInput
              data-testid="endValidityDateInput"
              disabled={isReadOnly || !isPointsRuleSelected}
              id="endValidityDate"
              label="Término da vigência dos pontos"
              min={getMinDateForPointsEndValidityDate()}
              onChange={handlePointsFieldChange('endDate')}
              onKeyDown={(e) => e.preventDefault()}
              readOnly={isReadOnly}
              required
              size="medium"
              type="date"
              value={values.points.endDate}
            />
          </Flex>
        )}
      </Flex>

      {isReadOnly && (
        <Flex direction="column" gap="$2">
          <HeadingAccelerator title="Atualizações" />
          <Flex direction="column" gap="$2">
            <FloraTypography as="p" fontSize="bodySmallStandard">
              Acelerador criado por {initialValues.responsible || ''} em{' '}
              {formatDateTimeWithLocale(initialValues.createdAt || '')}
            </FloraTypography>
            {initialValues.changeHistory?.map((change) => (
              <Flex key={change.updatedAt}>
                <FloraTypography as="p" fontSize="bodySmallStandard">
                  {`${getChangeHistoryAction(change)} por ${change.responsible} em ${formatDateTimeWithLocale(change.updatedAt)}.`}
                </FloraTypography>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

export default StepScoring
